<!--
  Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at
 
     http://www.apache.org/licenses/LICENSE-2.0
 
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
 
   SPDX-License-Identifier: Apache-2.0
-->

<div class="catalog-container">

    <div class="catalog-head d-flex align-items-center flex-wrap gap-3" [class.catalog-head--empty]="showCatalogEmpty">

        <!-- FILTER BUTTONS -->
        <div
            class="catalog-filter-panel"
            [class.catalog-filter-panel--active]="showFilters || hasActiveCatalogFilters()"
        >
            <button
                type="button"
                class="catalog-filter-trigger"
                [class.catalog-filter-trigger--active]="showFilters || hasActiveCatalogFilters()"
                [disabled]="showCatalogEmpty"
                (click)="toggleFilters()">
                {{ 'CATALOG.FILTER' | translate }}
                <span>{{ getVisibleCatalogRowsCount() }}</span>
            </button>

            <div class="filter-buttons" [class.show]="showFilters">

                <!-- CONNECTOR FILTER -->
                <div class="dropdown filter-dropdown">
                    <button
                        type="button"
                        class="catalog-filter-chip"
                        [class.catalog-filter-chip--active]="filters.connector.size > 0"
                        [disabled]="showCatalogEmpty"
                        (click)="toggleDropdown('connector')">
                        {{ 'CATALOG.CONNECTOR' | translate }}
                        <span>{{ getAvailableFilterValuesCount('connector') }}</span>
                    </button>

                    <ul
                        class="dropdown-menu"
                        [class.show]="openDropdown === 'connector'">
                        <li class="catalog-filter-dropdown-search">
                            <input
                                type="text"
                                class="form-control catalog-filter-dropdown-search__input"
                                [value]="filterOptionSearch.connector"
                                (click)="$event.stopPropagation()"
                                (input)="onFilterOptionSearch('connector', $event)"
                                [placeholder]="'GENERAL.SEARCH' | translate"
                                [attr.aria-label]="'GENERAL.SEARCH' | translate"
                            />
                        </li>

                        @for (connector of getVisibleFilterValues('connector'); track connector) {
                            <li class="dropdown-item">

                                <label class="catalog-filter-option">
                                    <input
                                        type="checkbox"
                                        class="form-check-input"
                                        [checked]="filters.connector.has(connector)"
                                        (click)="onFilterToggle('connector', connector, $event)"
                                    />

                                    <span>{{ connector }}</span>
                                </label>

                            </li>
                        }
                    </ul>

                </div>

                <!-- CONTENT TYPE FILTER -->
                <div class="dropdown filter-dropdown">
                    <button
                        type="button"
                        class="catalog-filter-chip"
                        [class.catalog-filter-chip--active]="filters.contentType.size > 0"
                        [disabled]="showCatalogEmpty"
                        (click)="toggleDropdown('contentType')">
                        {{ 'CATALOG.CONTENT-TYPE' | translate }}
                        <span>{{ getAvailableFilterValuesCount('contentType') }}</span>
                    </button>

                    <ul
                        class="dropdown-menu"
                        [class.show]="openDropdown === 'contentType'">
                        <li class="catalog-filter-dropdown-search">
                            <input
                                type="text"
                                class="form-control catalog-filter-dropdown-search__input"
                                [value]="filterOptionSearch.contentType"
                                (click)="$event.stopPropagation()"
                                (input)="onFilterOptionSearch('contentType', $event)"
                                [placeholder]="'GENERAL.SEARCH' | translate"
                                [attr.aria-label]="'GENERAL.SEARCH' | translate"
                            />
                        </li>

                        @for (type of getVisibleFilterValues('contentType'); track type) {
                            <li class="dropdown-item">
                                <label class="catalog-filter-option">
                                    <input
                                        type="checkbox"
                                        class="form-check-input"
                                        [checked]="filters.contentType.has(type)"
                                        (click)="onFilterToggle('contentType', type, $event)"
                                    />
                                    <span>{{ type }}</span>
                                </label>
                            </li>
                        }
                    </ul>
                </div>

            </div>

            <button
                type="button"
                class="catalog-filter-clear"
                [class.catalog-filter-clear--visible]="hasActiveCatalogFilters()"
                [disabled]="!hasActiveCatalogFilters()"
                (click)="clearCatalogFilters()"
                [attr.aria-hidden]="!hasActiveCatalogFilters()"
                [attr.aria-label]="'GENERAL.RESET-SEARCH' | translate"
                [attr.title]="'GENERAL.RESET-SEARCH' | translate"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                </svg>
            </button>
        </div>

        <!-- SEARCH -->
        <div
            class="catalog-search ms-auto"
            [class.catalog-search--empty-disabled]="showCatalogEmpty"
            [attr.title]="showCatalogEmpty ? null : ('CATALOG.SEARCH-SCOPE-HINT' | translate)">
            <div class="page-search-field" role="search">
                <span class="page-search-field__lead" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.3-4.3"/>
                    </svg>
                </span>
                <input
                    type="text"
                    class="form-control page-search-field__input"
                    #catalogSearcher
                    (input)="searchCatalog()"
                    [disabled]="showCatalogEmpty"
                    [placeholder]="'CATALOG.SEARCH' | translate"
                    [attr.aria-label]="'GENERAL.SEARCH' | translate"
                />
                <button
                    type="button"
                    class="page-search-field__reset"
                    (click)="resetSearch()"
                    [disabled]="showCatalogEmpty"
                    [attr.aria-label]="'GENERAL.RESET-SEARCH' | translate"
                    [attr.title]="'GENERAL.RESET-SEARCH' | translate"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466"/>
                    </svg>
                </button>
            </div>
        </div>

    </div>

    <!-- TABLE / empty state -->
    <div class="catalog-wrapper">
        @if (showCatalogEmpty) {
            <div class="catalog-empty-main" role="region" aria-labelledby="catalog-empty-title">
                <div class="catalog-empty-main__card">
                    <div class="catalog-empty-main__badge" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                            <ellipse cx="12" cy="5" rx="9" ry="3"/>
                            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                            <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/>
                        </svg>
                    </div>
                    <h2 class="catalog-empty-main__title" id="catalog-empty-title">{{ 'CATALOG.EMPTY-TITLE' | translate }}</h2>
                    @if (isCatalogAdministrator()) {
                        <p class="catalog-empty-main__lead">{{ 'CATALOG.EMPTY-ADMIN-LEAD' | translate }}</p>
                        <button type="button" class="btn catalog-empty-main__cta" (click)="goToAdministration()">
                            {{ 'CATALOG.EMPTY-ADMIN-LINK' | translate }}
                        </button>
                    } @else {
                        <p class="catalog-empty-main__lead">{{ 'CATALOG.EMPTY-USER-LEAD' | translate }}</p>
                    }
                </div>
            </div>
        } @else {
            <div class="catalog-body">
                @if (filteredAssets.length === 0) {
                    <div class="catalog-no-results" role="status">
                        <div class="catalog-no-results__icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="11" cy="11" r="7"/>
                                <path d="m20 20-3.5-3.5"/>
                                <path d="M8 11h6"/>
                            </svg>
                        </div>
                        <h2 class="catalog-no-results__title">{{ 'CATALOG.NO-RESULTS-TITLE' | translate }}</h2>
                        <p class="catalog-no-results__lead">{{ 'CATALOG.NO-RESULTS-LEAD' | translate }}</p>
                        @if (hasCatalogQuery()) {
                            <button type="button" class="btn catalog-no-results__clear" (click)="clearCatalogQuery()">
                                {{ 'CATALOG.NO-RESULTS-CLEAR' | translate }}
                            </button>
                        }
                    </div>
                } @else {
                <table class="table vertical-align" id="catalog-table">
                <thead>
                    <th
                        class="sortable-th"
                        id="table-header"
                        [class.asc]="sortCriteria === 'connector' && sortDirection === 'asc'"
                        [class.desc]="sortCriteria === 'connector' && sortDirection === 'desc'"
                        (click)="sortAssetsBy('connector')">
                        {{ 'CATALOG.CONNECTOR' | translate }}
                    </th>
                    <th
                        class="sortable-th"
                        id="table-header"
                        [class.asc]="sortCriteria === 'ID' && sortDirection === 'asc'"
                        [class.desc]="sortCriteria === 'ID' && sortDirection === 'desc'"
                        (click)="sortAssetsBy('ID')">
                        {{ 'CATALOG.ASSET.ID' | translate }}
                    </th>
                    <th
                        class="sortable-th"
                        id="table-header"
                        [class.asc]="sortCriteria === 'name' && sortDirection === 'asc'"
                        [class.desc]="sortCriteria === 'name' && sortDirection === 'desc'"
                        (click)="sortAssetsBy('name')">
                        {{ 'CATALOG.ASSET.NAME' | translate }}
                    </th>
                    <th
                        class="sortable-th"
                        id="table-header"
                        [class.asc]="sortCriteria === 'originator' && sortDirection === 'asc'"
                        [class.desc]="sortCriteria === 'originator' && sortDirection === 'desc'"
                        (click)="sortAssetsBy('originator')">
                        {{ 'CATALOG.ASSET.ORIGINATOR' | translate }}
                    </th>
                    <th
                        class="sortable-th"
                        id="table-header"
                        [class.asc]="sortCriteria === 'contentType' && sortDirection === 'asc'"
                        [class.desc]="sortCriteria === 'contentType' && sortDirection === 'desc'"
                        (click)="sortAssetsBy('contentType')">
                        {{ 'CATALOG.CONTENT-TYPE' | translate }}
                    </th>
                    <th class="catalog-actions-th">{{ 'CATALOG.ASSET.ACTIONS' | translate }}</th>
                </thead>

                <tbody>
                    @for (asset of filteredAssets; track asset) {
                        <tr>
                            <td>{{ asset.connector }}</td>
                            <td>{{ asset.ID }}</td>
                            <td>{{ asset.name }}</td>
                            <td class="text-center">
                                <div class="d-flex align-items-center justify-content-center gap-2 originator-cell">
                                    <span
                                        class="originator-truncate"
                                        [attr.title]="asset.originator ?? ''">{{ asset.originator }}
                                    </span>
                                    <button
                                        class="btn originator-copy-btn"
                                        [class.originator-copy-btn--copied]="copiedOriginatorAssetKey === getOriginatorCopyKey(asset)"
                                        (click)="copyToClipboard(asset.originator ?? '', getOriginatorCopyKey(asset))"
                                        data-bs-toggle="tooltip"
                                        [attr.title]="copiedOriginatorAssetKey === getOriginatorCopyKey(asset) ? 'Copiado' : 'Copiar'"
                                        [attr.aria-label]="copiedOriginatorAssetKey === getOriginatorCopyKey(asset) ? 'Copiado' : 'Copiar'">
                                        @if (copiedOriginatorAssetKey === getOriginatorCopyKey(asset)) {
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="originator-copy-btn__icon" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M6.97 11.03a.75.75 0 0 0 1.07-.02l3.992-4.99a.75.75 0 1 0-1.164-.944L7.477 9.417 5.383 7.323a.75.75 0 0 0-1.06 1.06z"/>
                                            </svg>
                                        } @else {
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="originator-copy-btn__icon" viewBox="0 0 16 16" aria-hidden="true">
                                                <path d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
                                                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zM10 8a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0zm-6 4a1 1 0 1 1 2 0v1a1 1 0 1 1-2 0zm4-3a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1"/>
                                            </svg>
                                        }
                                    </button>
                                </div>
                            </td>
                            <td>{{ asset.contentType }}</td>
                            <td class="text-center catalog-actions-cell">
                                <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap catalog-action-buttons">
                                    <button
                                        type="button"
                                        class="btn catalog-action-btn catalog-action-btn--info"
                                        (click)="openInfoAsset(asset)"
                                        [attr.title]="'CATALOG.ASSET.INFO' | translate"
                                        [attr.aria-label]="'CATALOG.ASSET.INFO' | translate">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi bi-info-circle catalog-action-icon" viewBox="0 0 16 16" aria-hidden="true">
                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn catalog-action-btn catalog-action-btn--offers"
                                        (click)="openOffersModal(asset)"
                                        [attr.title]="'CATALOG.ASSET.OFFERS' | translate"
                                        [attr.aria-label]="'CATALOG.ASSET.OFFERS' | translate">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi bi-journal-text catalog-action-icon" viewBox="0 0 16 16" aria-hidden="true">
                                            <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2z"/>
                                            <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1z"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn catalog-action-btn catalog-action-btn--props"
                                        (click)="openPropertiesModal(asset)"
                                        [attr.title]="'CATALOG.ASSET.PROPERTIES' | translate"
                                        [attr.aria-label]="'CATALOG.ASSET.PROPERTIES' | translate">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi bi-tags catalog-action-icon" viewBox="0 0 16 16" aria-hidden="true">
                                            <path d="M3 2v4.586l7 7L14.586 9l-7-7H3zM2 2a1 1 0 0 1 1-1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 2 6.586V2z"/>
                                            <path d="M5.5 5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm0 1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM1 7.086a1 1 0 0 0 .293.707L8.75 15.25l-.043.043a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 0 7.586V3a1 1 0 0 1 1-1v5.086z"/>
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
                }
        </div>
        }
    </div>

    @if (isLoading) {
        <div class="loading-overlay">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    }

</div>

<!-- Info Asset -->

<div class="modal fade" #infoAssetModal id="infoAssetModal" tabindex="-1" aria-labelledby="infoAssetModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
	<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-dialog-info-asset">
		<div class="modal-content info-asset-modal__content">
			<div class="modal-header info-asset-modal__header border-0">
				<div class="flex-grow-1 pe-2">
					<h5 class="modal-title title-3 mb-0 text-white" id="infoAssetModalLabel">{{ 'CATALOG.ASSET.INFO' | translate }}</h5>
					<p class="info-asset-modal__subtitle mb-0 mt-2">{{ 'CATALOG.ASSET.INFO-MODAL-HINT' | translate }}</p>
				</div>
				<button type="button" class="btn-close btn-close-white flex-shrink-0" [attr.aria-label]="'GENERAL.CLOSE' | translate" (click)="closeInfoAsset()"></button>
			</div>
			<div class="modal-body info-asset-modal__body">
				@if (selectedAsset) {
					<app-edc-info-modal-tabs tabGroupId="catalog-page-info-asset">
					<div class="edc-info-modal-tabs__summary info-asset-modal__summary">
						<h6 class="info-asset-modal__section-title">{{ 'CATALOG.ASSET.IDENTIFICATION' | translate }}</h6>
						<div class="card shadow-sm border-0 info-asset-modal__card">
							<div class="card-body">
								<dl class="row mb-0 info-asset-modal__definition-list">
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.ID' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.ID }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.NAME' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.name }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.CONTENT-TYPE' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.contentType }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.VERSION' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.version || '—' }}</dd>
								</dl>
							</div>
						</div>

						<h6 class="info-asset-modal__section-title">{{ 'CATALOG.ASSET.CONNECTION' | translate }}</h6>
						<div class="card shadow-sm border-0 info-asset-modal__card">
							<div class="card-body">
								<dl class="row mb-0 info-asset-modal__definition-list">
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.CONNECTOR' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.connector || '—' }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.ORIGINATOR' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.originator || '—' }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.URL' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.baseUrl || '—' }}</dd>
									<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.TYPE' | translate }}</dt>
									<dd class="col-sm-8 text-break">{{ selectedAsset.originalDTO.dataAddress.type || '—' }}</dd>
									@if (selectedAsset.originalDTO.dataAddress.name) {
										<dt class="col-sm-4 text-muted">{{ 'CATALOG.ASSET.DATA-ADDRESS-NAME' | translate }}</dt>
										<dd class="col-sm-8 text-break">{{ selectedAsset.originalDTO.dataAddress.name }}</dd>
									}
									@if (selectedAsset.originalDTO.dataAddress.proxyPath) {
										<dt class="col-sm-4 text-muted">proxyPath</dt>
										<dd class="col-sm-8 text-break">{{ selectedAsset.originalDTO.dataAddress.proxyPath }}</dd>
									}
									@if (selectedAsset.originalDTO.dataAddress.proxyQueryParams) {
										<dt class="col-sm-4 text-muted">proxyQueryParams</dt>
										<dd class="col-sm-8 text-break">{{ selectedAsset.originalDTO.dataAddress.proxyQueryParams }}</dd>
									}
									@if (selectedAsset.originalDTO.dataAddress.method) {
										<dt class="col-sm-4 text-muted">method</dt>
										<dd class="col-sm-8 text-break">{{ selectedAsset.originalDTO.dataAddress.method }}</dd>
									}
								</dl>
							</div>
						</div>

						@if (selectedAssetExtraPublicRows.length > 0) {
							<h6 class="info-asset-modal__section-title">{{ 'CATALOG.ASSET.PROPERTIES' | translate }}</h6>
							<div class="card shadow-sm border-0 info-asset-modal__card">
								<div class="card-body">
									<table class="table table-sm vertical-align mb-0 info-asset-modal__table">
										<thead>
											<tr>
												<th>{{ 'CATALOG.ASSET.PROPERTY-KEY' | translate }}</th>
												<th>{{ 'CATALOG.ASSET.PROPERTY-VALUE' | translate }}</th>
											</tr>
										</thead>
										<tbody>
											@for (row of selectedAssetExtraPublicRows; track row.key) {
												<tr>
													<td>{{ row.key }}</td>
													<td class="text-break">{{ row.value }}</td>
												</tr>
											}
										</tbody>
									</table>
								</div>
							</div>
						}

						@if (selectedAssetPrivateRows.length > 0) {
							<h6 class="info-asset-modal__section-title">{{ 'CATALOG.ASSET.PRIVATE-PROPERTIES' | translate }}</h6>
							<div class="card shadow-sm border-0 info-asset-modal__card">
								<div class="card-body">
									<table class="table table-sm vertical-align mb-0 info-asset-modal__table">
										<thead>
											<tr>
												<th>{{ 'CATALOG.ASSET.PROPERTY-KEY' | translate }}</th>
												<th>{{ 'CATALOG.ASSET.PROPERTY-VALUE' | translate }}</th>
											</tr>
										</thead>
										<tbody>
											@for (row of selectedAssetPrivateRows; track row.key) {
												<tr>
													<td>{{ row.key }}</td>
													<td class="text-break">{{ row.value }}</td>
												</tr>
											}
										</tbody>
									</table>
								</div>
							</div>
						}

					</div>
					<div class="edc-info-modal-tabs__raw asset-body info-asset-modal__raw">
						<app-edc-collapsible-json-view [jsonText]="selectedAssetJSON" />
					</div>
					</app-edc-info-modal-tabs>
				}
			</div>
			<div class="modal-footer info-asset-modal__footer detail-modal__footer border-0">
				<button type="button" class="btn btn-outline-light" (click)="closeInfoAsset()">{{ 'GENERAL.CLOSE' | translate }}</button>
			</div>
		</div>
	</div>
</div>

<!-- Propiedades del activo -->

<div class="modal fade" #propertiesAssetModal id="propertiesAssetModal" tabindex="-1" aria-labelledby="propertiesAssetModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
	<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-dialog-catalog-secondary">
		<div class="modal-content detail-modal__content">
			<div class="modal-header detail-modal__header border-0">
				<div class="flex-grow-1 pe-2">
					<h5 class="modal-title title-3 mb-0 text-white" id="propertiesAssetModalLabel">{{ 'CATALOG.ASSET.PROPERTIES' | translate }}</h5>
					<p class="detail-modal__subtitle mb-0 mt-2">{{ 'CATALOG.ASSET.PROPERTIES-MODAL-HINT' | translate }}</p>
				</div>
				<button type="button" class="btn-close btn-close-white flex-shrink-0" [attr.aria-label]="'GENERAL.CLOSE' | translate" (click)="closePropertiesModal()"></button>
			</div>
			<div class="modal-body detail-modal__body">
				@if (selectedPropertyRows.length === 0) {
					<p class="text-muted mb-0">{{ 'CATALOG.ASSET.PROPERTIES-EMPTY' | translate }}</p>
				} @else {
					<div class="card border-0 detail-modal__panel">
						<div class="card-body p-3 p-md-4">
							<div class="detail-modal__kv">
								@for (row of selectedPropertyRows; track row.key) {
									<div class="detail-modal__kv-row">
										<div class="detail-modal__kv-key text-break">
											@if (row.labelKey) {
												{{ row.labelKey | translate }}
											} @else {
												{{ humanizePropertyKey(row.key) }}
											}
										</div>
										<div class="detail-modal__kv-val detail-modal__kv-val--mono text-break">{{ row.value }}</div>
									</div>
								}
							</div>
						</div>
					</div>
				}
			</div>
			<div class="modal-footer detail-modal__footer border-0">
				<button type="button" class="btn btn-outline-light" (click)="closePropertiesModal()">{{ 'GENERAL.CLOSE' | translate }}</button>
			</div>
		</div>
	</div>
</div>

<!-- Ofertas del activo -->

<div class="modal fade" #offersAssetModal id="offersAssetModal" tabindex="-1" aria-labelledby="offersAssetModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
	<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-dialog-catalog-offers">
		<div class="modal-content detail-modal__content">
			<div class="modal-header detail-modal__header border-0">
				<div class="flex-grow-1 pe-2">
					<h5 class="modal-title title-3 mb-0 text-white" id="offersAssetModalLabel">{{ 'CATALOG.ASSET.OFFERS' | translate }}</h5>
					<p class="detail-modal__subtitle mb-0 mt-2">{{ 'CATALOG.ASSET.OFFERS-MODAL-HINT' | translate }}</p>
				</div>
				<button type="button" class="btn-close btn-close-white flex-shrink-0" [attr.aria-label]="'GENERAL.CLOSE' | translate" (click)="closeOffersModal()"></button>
			</div>
			<div class="modal-body detail-modal__body catalog-offers-modal__body">
				@if (selectedOffersSummaries.length === 0) {
					<p class="text-muted mb-0">{{ 'CATALOG.ASSET.NO-OFFERS' | translate }}</p>
				} @else {
					<div class="catalog-offers-list">
						@for (offer of selectedOffersSummaries; track offer.index) {
							<div class="catalog-offer-card">
								<div class="catalog-offer-card__head">
									<h6 class="catalog-offer-card__title">{{ 'CATALOG.ASSET.OFFER-TITLE' | translate: { n: offer.index + 1 } }}</h6>
									@if (offer.offerId) {
										<p class="detail-modal__section-label catalog-offer-card__section-label">{{ 'CATALOG.ASSET.OFFER-ID-LABEL' | translate }}</p>
										<div class="catalog-offer-card__id-row d-flex align-items-center gap-2 min-w-0">
											<code class="catalog-offer-card__id flex-grow-1 min-w-0" [attr.title]="offer.offerId">{{ offer.offerId }}</code>
											<button
												type="button"
												class="btn originator-copy-btn catalog-offer-card__copy flex-shrink-0"
												[class.originator-copy-btn--copied]="copiedOfferIndex === offer.index"
												(click)="copyOfferIdToClipboard(offer.offerId, offer.index)"
												[attr.title]="copiedOfferIndex === offer.index ? 'Copiado' : ('CATALOG.ASSET.COPY-OFFER-ID' | translate)"
												[attr.aria-label]="copiedOfferIndex === offer.index ? 'Copiado' : ('CATALOG.ASSET.COPY-OFFER-ID' | translate)">
												@if (copiedOfferIndex === offer.index) {
													<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="originator-copy-btn__icon" viewBox="0 0 16 16" aria-hidden="true">
														<path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M6.97 11.03a.75.75 0 0 0 1.07-.02l3.992-4.99a.75.75 0 1 0-1.164-.944L7.477 9.417 5.383 7.323a.75.75 0 0 0-1.06 1.06z"/>
													</svg>
												} @else {
													<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="originator-copy-btn__icon" viewBox="0 0 16 16" aria-hidden="true">
														<path d="M6.5 0A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0zm3 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5z"/>
														<path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1A2.5 2.5 0 0 1 9.5 5h-3A2.5 2.5 0 0 1 4 2.5zM10 8a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0zm-6 4a1 1 0 1 1 2 0v1a1 1 0 1 1-2 0zm4-3a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1"/>
													</svg>
												}
											</button>
										</div>
									}
									<p class="detail-modal__section-label catalog-offer-card__constraints-heading">{{ 'CATALOG.ASSET.OFFER-CONSTRAINTS-LABEL' | translate }}</p>
									@if (offer.lines.length) {
										<ul class="catalog-offer-card__constraints mb-0">
											@for (line of offer.lines; track $index) {
												<li>{{ line }}</li>
											}
										</ul>
									} @else {
										<p class="text-muted mb-0 catalog-offer-card__empty">{{ 'CATALOG.ASSET.POLICY-NONE' | translate }}</p>
									}
								</div>
							</div>
						}
					</div>
				}
			</div>
			<div class="modal-footer detail-modal__footer border-0">
				<button type="button" class="btn btn-outline-light" (click)="closeOffersModal()">{{ 'GENERAL.CLOSE' | translate }}</button>
			</div>
		</div>
	</div>
</div>

<!-- Toast -->

<div class="toast-container position-fixed top-0 end-0 p-3">
	<div
		#toastElement
		class="toast align-items-center text-bg-success border-0"
		role="alert"
		aria-live="assertive"
		aria-atomic="true"
	>
		<div class="d-flex">
			<div class="toast-body">
				{{ toastMessage }}
			</div>
			<button
				type="button"
				class="btn-close btn-close-white me-2 m-auto"
				data-bs-dismiss="toast"
				aria-label="Close"
			>
			</button>
		</div>
  	</div>
</div>